<script setup lang="ts">
import { computed } from 'vue'
import { Tags } from '@lucide/vue'
import { Badge, Button, PickerDialog } from 'catalyst-vue'
import type { Category } from '@tanko/shared'
import { useLibraryStore } from '../../stores/library'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import { useControlSize } from '../../lib/library-layout'

/**
 * 書庫頁的分類篩選入口。管理（新增、更名、刪除）在分類管理頁，不放在這裡。
 * 用 PickerDialog 取得可搜尋的多選，分類數量成長後仍找得到。
 */

const open = defineModel<boolean>('open', { default: false })
const local = useLibraryStore()
const cloud = useCloudLibraryStore()
const entries = useLibraryEntriesStore()
const controlSize = useControlSize()

// 分類的真相在雲端目錄；雲端還沒回來時用本機鏡像，篩選才不會在離線時整個不能用。
const categories = computed<Category[]>(() => cloud.categories.length ? cloud.categories : local.categories)
const ready = computed(() => local.ready)
const selected = computed({
  get: () => entries.selectedCategories,
  set: value => { entries.selectedCategories = value },
})
const selectedCategories = computed(() => categories.value.filter(category => selected.value.includes(category.id)))
const label = computed(() => selectedCategories.value.length ? `分類 · ${selectedCategories.value.length}` : '分類篩選')

function apply(keys: (string | number)[]) {
  const valid = new Set(categories.value.map(category => category.id))
  selected.value = keys.map(String).filter(id => valid.has(id))
}
</script>

<template>
  <div class="category-filter">
    <Button outline :size="controlSize" :disabled="!ready" aria-haspopup="dialog" @click="open = true">
      <Tags :size="16" aria-hidden="true" />{{ label }}
    </Button>
    <!--
      徽章旁不再放第二個清除鈕：工具列那一個帶文字的距離只有幾十像素，而兩者清的範圍
      不同（一個只清分類、一個清全部），使用者在畫面上分辨不出來。要只清分類的人可以
      在篩選器裡按清空，那裡的範圍是明確的。
    -->
    <div v-if="selectedCategories.length" class="category-filter-badges">
      <Badge v-for="category in selectedCategories" :key="category.id" :color="category.private ? 'warning' : 'primary'">{{ category.private ? `${category.name}（隱私）` : category.name }}</Badge>
    </div>
    <PickerDialog
      :open="open"
      :items="categories"
      :model-value="selected"
      :item-key="(category: Category) => category.id"
      :item-text="(category: Category) => category.private ? `${category.name}（隱私）` : category.name"
      selection-mode="multiple"
      title="篩選分類"
      description="可複選，顯示屬於任一選中分類的作品。標示「隱私」的分類，其作品只有選中它時才會出現在書庫。分類的新增與刪除請到分類管理。"
      search-label="搜尋分類"
      search-placeholder="輸入分類名稱"
      empty-message="沒有符合的分類"
      confirm-label="套用篩選"
      @apply="apply"
      @update:open="open = $event"
    />
  </div>
</template>
